import {
  Breakpoints,
  compact,
  formatPrice,
  formatTimeFromMinutes,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { type FC, useMemo } from 'react';

import {
  type SkuFamily,
  SkuTier,
  SubscriptionInterval,
} from '../../__generated-gql-types__/globalTypes';
import { type SkuMapSkuFragment } from '../../utils/maps/__generated-gql-types__/sku-map.generated';
import { type SkuMapSkusByTier } from '../../utils/maps/sku-map';

import { RadioToggle, type RadioToggleOption } from './RadioToggle';
import { useSelectHoursInputSkusQuery } from './__generated-gql-types__/SelectHoursInput.generated';

const SESSION_NUMBERS_TO_DISPLAY = [1, 2, 4, 6, 8];

interface UseSelectHoursInputHookProps {
  tier: SkuTier;
  period: Nullable<
    SubscriptionInterval.MONTHLY | SubscriptionInterval.ONE_TIME
  >;
  justThisSessionTime?: number;
  justThisSessionPrice?: number;
  subscriptionTimeInMinutes?: number;
  singleHourPrice: number;
  family?: SkuFamily;
  displayMonths?: boolean;
  getRowLabel?: (sku: SkuMapSkuFragment) => string;
  discount?: number;
}
export const useSelectHoursInput = ({
  tier,
  period,
  justThisSessionTime,
  justThisSessionPrice,
  subscriptionTimeInMinutes,
  singleHourPrice,
  family,
  displayMonths,
  getRowLabel,
  discount = 0,
}: UseSelectHoursInputHookProps) => {
  const isSm = useMediaQuery(Breakpoints.SM);

  const { data: initialLoadData } = useSelectHoursInputSkusQuery({
    variables: { tier: tier, period: period, family: family },
  });

  const skusByTier: SkuMapSkusByTier = useMemo(() => {
    const sbt: SkuMapSkusByTier = {
      [SkuTier.BASE]: {
        sessionNumberOptions: [],
      },
      [SkuTier.PRO]: {
        sessionNumberOptions: [],
      },
      [SkuTier.ELITE]: {
        sessionNumberOptions: [],
      },
      [SkuTier.ELITE_PLUS]: {
        sessionNumberOptions: [],
      },
    };
    initialLoadData?.skus.results.forEach((sku) => {
      if (sku.numberOfSessions > 9) return;
      sbt[sku.tier].sessionNumberOptions[sku.numberOfSessions] = sku;
    });
    return sbt;
  }, [initialLoadData?.skus.results]);

  const skus = useMemo(
    () => Object.values(skusByTier[tier].sessionNumberOptions),
    [skusByTier, tier],
  );

  const hoursOfCoachingOptions: RadioToggleOption[] = useMemo(() => {
    return tier
      ? [
          ...(justThisSessionTime &&
          justThisSessionPrice &&
          period === SubscriptionInterval.ONE_TIME
            ? [
                {
                  label: `Just this session - ${formatTimeFromMinutes(justThisSessionTime)}`,
                  rightContent: (
                    <NonSubsRadioRightContent
                      formattedPrice={formatPrice(justThisSessionPrice)}
                    />
                  ),
                  value: justThisSessionTime,
                },
              ]
            : []),
          ...Object.values(skusByTier[tier].sessionNumberOptions)
            .filter((value) =>
              SESSION_NUMBERS_TO_DISPLAY.includes(value.numberOfSessions),
            )
            .map((value) => {
              if (
                justThisSessionTime &&
                // allow subscribing for `justThisSessionTime` if it's exactly the same as the number of sessions
                // if a la carte, `justThisSessionTime` will be handled by option above
                (period === SubscriptionInterval.ONE_TIME
                  ? justThisSessionTime >= value.numberOfSessions * 60
                  : justThisSessionTime > value.numberOfSessions * 60)
              )
                return null;
              const price = value.price * (1 - discount);
              const basePrice = singleHourPrice;
              const originalPrice = basePrice * value.numberOfSessions;
              const basePriceDiscount = originalPrice - value.price;

              const label =
                getRowLabel?.(value) ??
                `${formatTimeFromMinutes(value.timeInMinutes)}${
                  isSm ? ' of coaching' : ''
                }`;

              return {
                label,
                rightContent: (
                  <NonSubsRadioRightContent
                    formattedPrice={`${displayMonths ? `${formatPrice(price)} per month` : `${formatPrice(value.unitPrice)}/hr`}`}
                    discount={
                      basePrice && basePriceDiscount > 0
                        ? basePriceDiscount
                        : undefined
                    }
                    current={
                      subscriptionTimeInMinutes !== undefined
                        ? subscriptionTimeInMinutes === value.timeInMinutes
                        : false
                    }
                  />
                ),
                value:
                  period === SubscriptionInterval.ONE_TIME
                    ? value.numberOfSessions * 60
                    : value.id,
              };
            }),
        ].filter(compact)
      : [];
  }, [
    tier,
    justThisSessionTime,
    justThisSessionPrice,
    period,
    skusByTier,
    singleHourPrice,
    discount,
    getRowLabel,
    isSm,
    displayMonths,
    subscriptionTimeInMinutes,
  ]);

  return {
    hoursOfCoachingOptions,
    skus,
  };
};

interface SelectHoursInputProps {
  onChange: (value: number | string) => void;
  value: number | Nullable<string>;
  hoursOfCoachingOptions: RadioToggleOption[];
}

export const SelectHoursInput: FC<SelectHoursInputProps> = ({
  onChange,
  value,
  hoursOfCoachingOptions,
}) => {
  return (
    <RadioToggle
      options={hoursOfCoachingOptions}
      value={value}
      onChange={onChange}
      name="hours-of-coaching"
    />
  );
};

interface NonSubsRadioRightContentProps {
  discount?: number;
  formattedPrice: string;
  current?: boolean;
}

const NonSubsRadioRightContent: FC<NonSubsRadioRightContentProps> = ({
  discount,
  formattedPrice,
  current,
}) => {
  return (
    <div className="flex items-center space-x-2">
      {discount ? (
        <div className="flex items-center justify-center rounded bg-leland-primary/25 px-1.5 text-sm font-medium text-leland-dark-green">
          Save {formatPrice(discount)}
        </div>
      ) : null}
      {current ? (
        <div className="flex items-center justify-center rounded bg-leland-gray-hover px-1.5 py-1 text-sm font-medium text-leland-gray-light">
          Current
        </div>
      ) : null}
      <p className="text-leland-gray-light">{formattedPrice}</p>
    </div>
  );
};
